import { Input } from '../components/ui/input';
import { FormField } from '../components/ui/FormField';
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { isTenantFixedByHostname, useTenant } from '../hooks/useTenantContext';
import Button from '../components/ui/Button';
import { getTenantHeaders } from '../utils/tenantApiHeaders';
import { apiClient } from '../services/api';
import logo from '../assets/logo.png';

function organizationDisplayTitle(
  tenantSlug: string,
  displayLabel: string
): string {
  if (!tenantSlug) {
    return 'Enter your organization';
  }
  return displayLabel
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

const LoginPage: React.FC = () => {
  const { user, isLoading, login } = useAuth();
  const navigate = useNavigate();
  const { displayLabel, tenantSlug, setTenantSlug } = useTenant();
  const organizationTitle = organizationDisplayTitle(tenantSlug, displayLabel);

  // Local login state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [localLoading, setLocalLoading] = useState(false);
  const [error, setError] = useState('');

  React.useEffect(() => {
    if (!isTenantFixedByHostname() && !tenantSlug) {
      navigate('/', { replace: true });
    }
  }, [tenantSlug, navigate]);

  // Redirect if already authenticated
  React.useEffect(() => {
    if (user && !isLoading) {
      navigate('/portal');
    }
  }, [user, isLoading, navigate]);

  // Use centralized API URL
  const apiUrl = apiClient.getBaseUrl();

  const handleGoogleLogin = () => {
    window.location.href = `${apiUrl}/auth/google`;
  };

  const handleLocalLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalLoading(true);
    setError('');

    try {
      // eslint-disable-next-line no-console
      console.log('Attempting login with API URL:', apiUrl);
      // eslint-disable-next-line no-console
      console.log('Login data:', { email, password: '***' });

      const response = await fetch(`${apiUrl}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getTenantHeaders(),
        },
        body: JSON.stringify({ email, password }),
      });

      // Response received
      const data = await response.json();
      // Response data received

      if (response.ok) {
        login(data.access_token);
        navigate('/portal');
      } else {
        setError(data.detail || 'Login failed');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(
        `Network error: ${err instanceof Error ? err.message : 'Please try again.'}`
      );
    } finally {
      setLocalLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-ink-subtle">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <img src={logo} alt="" className="h-14 w-auto mx-auto logo-themed" />
          <h2 className="mt-6 text-center text-3xl font-extrabold text-ink">
            Slide Rule Engineering Platform
          </h2>
          <p className="mt-2 text-center text-sm text-ink-subtle">
            Intelligent Engineering
          </p>
        </div>

        <div
          className="rounded-xl border border-primary-600/35 bg-primary-600/[0.08] px-4 py-3 text-center shadow-sm"
          role="status"
          aria-live="polite"
        >
          <p className="text-[0.65rem] font-semibold uppercase tracking-wider text-ink-subtle">
            Organization
          </p>
          <p className="mt-1 text-lg font-semibold text-ink tracking-tight">
            {organizationTitle}
          </p>
          {tenantSlug !== '' && (
            <p
              className="mt-1 text-xs text-ink-subtle font-mono"
              title="Organization identifier"
            >
              {tenantSlug}
            </p>
          )}
          {!isTenantFixedByHostname() && (
            <p className="mt-3 text-center">
              <Link
                to="/"
                className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
                onClick={() => setTenantSlug('')}
              >
                {!tenantSlug
                  ? 'Select organization…'
                  : 'Choose a different organization'}
              </Link>
            </p>
          )}
        </div>

        <div className="bg-surface py-8 px-6 shadow rounded-lg border border-border">
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-medium text-ink">
                Sign in to your account
              </h3>
              <p className="mt-2 text-sm text-ink-subtle">
                Access restricted to authorized personnel only
              </p>
            </div>

            {/* Local Login Form */}
            <form onSubmit={handleLocalLogin} className="space-y-4">
              <FormField id="email" label="Email Address">
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-1 block w-full px-3 py-2 border border-border rounded-md shadow-sm bg-surface-strong text-ink placeholder-ink-subtle focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 input-neon-focus"
                  placeholder="Enter your email"
                />
              </FormField>
              <FormField id="password" label="Password">
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="mt-1 block w-full px-3 py-2 border border-border rounded-md shadow-sm bg-surface-strong text-ink placeholder-ink-subtle focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 input-neon-focus"
                  placeholder="Enter your password"
                />
              </FormField>

              {error && (
                <div className="text-danger-600 text-sm text-center">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={localLoading}
                className="w-full btn-neon-outline"
              >
                {localLoading ? 'Signing in...' : 'Sign in'}
              </Button>
            </form>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-surface text-ink-subtle">Or</span>
              </div>
            </div>

            <div>
              <Button
                onClick={handleGoogleLogin}
                className="w-full flex justify-center items-center"
                variant="outline"
              >
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Sign in with Google
              </Button>
            </div>
          </div>
        </div>

        <div className="text-center space-y-3">
          <p className="text-xs text-ink-subtle">
            This system is for authorized users only. All activities are logged
            and monitored.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
